import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ExternalLink } from 'lucide-react';


export default function PeptideDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [peptide, setPeptide] = useState(null);

  useEffect(() => {
    fetch(`${API}/peptides/${id}`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => setPeptide(data))
      .catch(console.error);
  }, [id]);

  if (!peptide) return <div className="p-8">Loading...</div>;

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto" data-testid="peptide-detail-page">
      <Button variant="ghost" onClick={() => navigate('/peptides')} className="mb-6" data-testid="back-button">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Database
      </Button>

      <Card className="p-6 md:p-8 glass-card">
        <div className="flex items-start justify-between mb-6">
          <div>
            <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary border border-primary/30">
              {peptide.category}
            </span>
            <h1 className="text-4xl font-bold mt-3 mb-2">{peptide.name}</h1>
            {peptide.aliases.length > 0 && (
              <p className="text-muted-foreground">{peptide.aliases.join(' • ')}</p>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-bold mb-3">Summary</h2>
            <p className="text-muted-foreground">{peptide.summary}</p>
          </div>

          <div>
            <h2 className="text-xl font-bold mb-3">Key Benefits</h2>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              {peptide.benefits.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-bold mb-2">Dosage Range</h3>
              <p className="font-mono text-sm text-muted-foreground">{peptide.dosage_range}</p>
            </div>
            <div>
              <h3 className="font-bold mb-2">Half-Life</h3>
              <p className="font-mono text-sm text-muted-foreground">{peptide.half_life}</p>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-bold mb-3">Reconstitution</h2>
            <p className="text-muted-foreground">{peptide.reconstitution}</p>
          </div>

          <div>
            <h2 className="text-xl font-bold mb-3">Storage</h2>
            <p className="text-muted-foreground">{peptide.storage}</p>
          </div>

          <div>
            <h2 className="text-xl font-bold mb-3">Side Effects</h2>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              {peptide.side_effects.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>

          <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/30">
            <h3 className="font-bold text-destructive mb-2">Warnings</h3>
            <p className="text-sm text-destructive">{peptide.warnings}</p>
          </div>

          {peptide.pubmed_links.length > 0 && (
            <div>
              <h2 className="text-xl font-bold mb-3">Research Links</h2>
              <div className="space-y-2">
                {peptide.pubmed_links.map((link, i) => (
                  <a
                    key={i}
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-primary hover:underline"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    PubMed Study {i + 1}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
